
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from "chart.js";
import axios from "../../api/axios";
import { getAttributeValue } from "../../utils/utils";
import APRmemo from "./APRComment.js/APRmemo";
import APRComment from "./APRComment.js/AprComments";
import AprViewToggle, { isBlankDimension } from "./AprViewToggle";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const PROJECT_PROGRAM = "g3wMUKEMmH3";
const PROJECT_TYPE_ATTRIBUTE = "wtf3BR2YO8w";
const PROJECT_TYPE_VALUE = "Project";

const departments = [
  "Central Administration",
  "Education, Youth and Sports",
  "Health",
  "Human Resources",
  "Urban Roads",
  "Works",
];

const Table_5 = forwardRef(({ year, district, period, hideTableDis }, ref) => {

  const [tableData, setTableData] = useState([]);

  const selectedYear = useMemo(() => {
    const parsed = parseInt(year, 10);
    return Number.isFinite(parsed) ? parsed : new Date().getFullYear();
  }, [year]);

  useEffect(() => {
    getProjects();
  }, [selectedYear, district, period]);

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "Table_5",
      tableData
    }),
  }));

  async function getProjects() {
    if (!district) {
      setTableData([]);
      return;
    }

    try {
      const startDate = `${selectedYear}-01-01`;
      const endDate = `${selectedYear}-12-31`;
      const [entitiesResp, eventsResp] = await Promise.all([
        axios.get(
          `/tracker/trackedEntities?orgUnit=${district}&program=${PROJECT_PROGRAM}&filter=${PROJECT_TYPE_ATTRIBUTE}:EQ:${PROJECT_TYPE_VALUE}&startDate=${startDate}&endDate=${endDate}&pageSize=5000`,
        ),
        axios.get(
          `/tracker/events?program=${PROJECT_PROGRAM}&orgUnit=${district}&startDate=${startDate}&endDate=${endDate}&pageSize=5000`,
        ),
      ]);

      const projects = entitiesResp.data.instances || [];
      const reports = eventsResp.data.instances || [];

      if (projects.length === 0) {
        setTableData([]);
        return;
      }

      const temp = projects.map((project, idx) => {
        const currentReports = reports.filter(
          (report) => report.trackedEntity === project.trackedEntity,
        );
        let projectStatus = "";

        currentReports.forEach((report) => {
          report.dataValues?.forEach((dataValue) => {
            if (dataValue.dataElement === "tE3QKB203nh") {
              projectStatus = dataValue.value;
            }
          });
        });

        return {
          no: idx + 1,
          expectedStart: getAttributeValue("Expected Start Date", project),
          expectedCompletion: getAttributeValue("Expected Completion Date", project),
          department: getAttributeValue("Department", project),
          collaboratingDepartments: getAttributeValue("Collaborating Department", project),
          projectStatus,
        };
      });

      setTableData(groupDataByDepartments(temp));
    } catch (err) {
      console.error("Error loading Table 5 projects:", err);
      setTableData([]);
    }
  }

  const groupDataByDepartments = (projects) => {

    const grouped = {};
    const temp = [];

    departments.forEach(dep => {
      const departmentProjects = projects.filter(
        project => project.department && project.department.includes(dep)
      );

      let rolloverCounter = 0;
      let newCounter = 0;
      let collaboratingDep = "";

      departmentProjects.forEach(p => {
        if (p.expectedStart.includes(selectedYear)) {
          newCounter += 1;
          collaboratingDep += p.collaboratingDepartments !== 'N/A' ? `${p.collaboratingDepartments}, ` : ""
        }

        if (!p.expectedStart.includes(selectedYear)) {
          const projectYear = new Date(p.expectedCompletion).getFullYear();
          if ((projectYear < selectedYear) && !p.projectStatus.includes("Completed")) {
            rolloverCounter += 1;
            collaboratingDep += p.collaboratingDepartments !== 'N/A' ? `${p.collaboratingDepartments}, ` : ""
          }
        }


      });

      grouped[dep] = departmentProjects;
      const tempDataSet = {
        department: dep,
        rollover: rolloverCounter,
        new: newCounter,
        totalProjects: parseInt(rolloverCounter) + parseInt(newCounter),
        collaboratingDepartment: collaboratingDep
      };

      temp.push(tempDataSet);

    });

    let newTotal = 0;
    let rolloverTotal = 0;

    temp.forEach(tp => {
      newTotal += tp.new;
      rolloverTotal += tp.rollover
    });

    const total = {
      department: "Total",
      rollover: rolloverTotal,
      new: newTotal,
      totalProjects: rolloverTotal + newTotal,
      collaboratingDepartment: ""
    };

    temp.push(total);

    return temp;
  }

  const chartRows = tableData.filter((row) => row.department !== "Total");
  const chartData = {
    labels: chartRows.map((row) => row.department),
    datasets: [
      {
        label: "Rollover Projects",
        data: chartRows.map((row) => row.rollover),
        backgroundColor: "#ff9800",
        borderColor: "#e07a00",
        borderWidth: 1,
      },
      {
        label: "New Projects",
        data: chartRows.map((row) => row.new),
        backgroundColor: "#28a745",
        borderColor: "#1e7e34",
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: `Rollover and New Projects by Department, ${selectedYear}`,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { precision: 0 },
        title: {
          display: true,
          text: "Number of Projects",
        },
      },
    },
  };

  const displayRows = tableData.filter((row) => !isBlankDimension(row.department) || row.department === "Total");

  const tableMarkup = (
    <div className="table-responsive">
      <table className="table table-bordered">
        <thead style={{ backgroundColor: "#d4edda", fontWeight: "bold", border: "1px solid #000" }}>
          <tr>
            <th rowSpan="2" style={{ border: "1px solid #000", fontWeight: "bold", verticalAlign: "middle" }}>
              Departments
            </th>
            <th colSpan="2" style={{ border: "1px solid #000", fontWeight: "bold", textAlign: "center" }}>
              No. of projects
            </th>
            <th rowSpan="2" style={{ border: "1px solid #000", fontWeight: "bold", verticalAlign: "middle" }}>
              Total
            </th>
            <th rowSpan="2" style={{ border: "1px solid #000", fontWeight: "bold", verticalAlign: "middle" }}>
              Collaborating Department
            </th>
          </tr>
          <tr>
            <th style={{ border: "1px solid #000", fontWeight: "bold" }}>Rollover</th>
            <th style={{ border: "1px solid #000", fontWeight: "bold" }}>New</th>
          </tr>
        </thead>
        <tbody>
          {displayRows.map((row, index) => (
            <tr key={index}>
              <td style={{ border: "1px solid #000" }}>{row.department}</td>
              <td style={{ border: "1px solid #000" }}>{row.rollover}</td>
              <td style={{ border: "1px solid #000" }}>{row.new}</td>
              <td style={{ border: "1px solid #000" }}>{row.totalProjects}</td>
              <td style={{ border: "1px solid #000" }}>{row.collaboratingDepartment}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const chartMarkup = (
    <div>
      <h4>Figure 5: Distribution of Projects by Department</h4>
      <Bar data={chartData} options={chartOptions} />
    </div>
  );

  return (
    <div className="col-12">
      <h3>Table 5 – Distribution of Physical projects among departments of the Assembly</h3>
      <div className="card">
        <div className="card-header"></div>
        <div className="card-body">
          <p style={{ textAlign: "justify", marginBottom: "1rem" }}>
            This table shows how physical projects are distributed across Assembly departments for the year under review.
            Rollover projects are those carried forward from earlier years, while new projects are those started in {selectedYear}.
            Collaborating departments are listed where more than one department is involved in implementation.
          </p>
          <APRmemo
            year={year}
            districtId={district}
            tableCommentedId={`table5-${year}`}
            hideTableDis={hideTableDis}
          />
          <AprViewToggle
            tableContent={tableMarkup}
            chartContent={chartMarkup}
            csvFileName={`table5-projects-by-department-${selectedYear}`}
            csvHeaders={["Department", "Rollover", "New", "Total", "Collaborating Department"]}
            csvRows={displayRows.map((row) => [
              row.department,
              row.rollover,
              row.new,
              row.totalProjects,
              row.collaboratingDepartment,
            ])}
          />

          <APRComment
            data={tableData}
            year={year}
            districtId={district}
            tableCommentedId={`table5-${year}`}
            hideTableDis={hideTableDis}
          >
            {({ renderCommentInput, renderCommentList }) => (
              <>
                {renderCommentInput()}
                {renderCommentList()}
              </>
            )}
          </APRComment>
        </div>
      </div>
    </div>
  );
});

export default Table_5;
